import React, { useEffect, useState } from 'react';
import { ChevronsUp, Copy, FilePlus2, Save, X } from 'lucide-react';
import ErrorMessage from './ErrorMessage';
import apiClient from '../services/apiClient';
import PracticeSettingsService from '../services/PracticeSettingsService';
import { t } from '../i18n';
import './FlashCardForm.css';
import './LevelUpFlashCardForm.css';

function normalizeContent(value) {
  return String(value || '').trim();
}

function getInitialContent(initialData) {
  const sourceType = PracticeSettingsService.getCardType(initialData || {});
  return {
    contextPre: normalizeContent(initialData?.suggestedContextPre ?? initialData?.contextPre),
    question: normalizeContent(initialData?.suggestedQuestion ?? initialData?.front),
    memo: normalizeContent(initialData?.memo),
    answer: normalizeContent(initialData?.suggestedAnswer ?? initialData?.back),
    backMemo: normalizeContent(initialData?.backMemo),
    contextAfter: normalizeContent(initialData?.suggestedContextAfter ?? initialData?.contextAfter),
    cardType: sourceType === PracticeSettingsService.CARD_TYPES.SIMPLE
      ? PracticeSettingsService.CARD_TYPES.SIMPLE
      : PracticeSettingsService.CARD_TYPES.NORMAL,
  };
}

function validatePayload(payload, mode, copy) {
  if (!payload.question) return copy('questionBlank', 'Question cannot be blank.');
  if (!payload.answer) return copy('answerBlank', 'Answer cannot be blank.');
  if (payload.question.length > 500) return copy('questionLong', 'Question cannot exceed 500 characters.');
  if (payload.answer.length > 500) return copy('answerLong', 'Answer cannot exceed 500 characters.');
  if (payload.contextPre?.length > 500 || payload.memo?.length > 500 || payload.backMemo?.length > 500 || payload.contextAfter?.length > 500) {
    return copy('contextLong', 'Memo and context fields cannot exceed 500 characters.');
  }
  if (mode !== 'edit' && !payload.targetNoteId && !payload.newNoteTitle) {
    return copy('destinationRequired', 'Choose a target drill or enter a new drill name.');
  }
  return '';
}

function LevelUpFlashCardForm({ initialData, onLevelUp, onSubmit, onCancel, mode = 'levelUp', presentation = 'default' }) {
  // The dedicated page opts into new copy; Triumph and ordinary edit forms keep their existing presentation.
  const isLevelUpPage = presentation === 'levelUpPage' && mode !== 'edit';
  const nextLevel = Math.min(8, PracticeSettingsService.getCardLevel(initialData) + 1);
  const copy = (key, fallback, values) => isLevelUpPage
    ? t(`learningNavigation.levelUp.form.${key}`, undefined, values)
    : fallback;
  const [contextPre, setContextPre] = useState('');
  const [question, setQuestion] = useState('');
  const [memo, setMemo] = useState('');
  const [answer, setAnswer] = useState('');
  const [backMemo, setBackMemo] = useState('');
  const [contextAfter, setContextAfter] = useState('');
  const [cardType, setCardType] = useState(PracticeSettingsService.CARD_TYPES.NORMAL);
  const [availableNotes, setAvailableNotes] = useState([]);
  const [targetMode, setTargetMode] = useState('new');
  const [targetNoteId, setTargetNoteId] = useState('');
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const content = getInitialContent(initialData);
    setContextPre(content.contextPre);
    setQuestion(content.question);
    setMemo(content.memo);
    setAnswer(content.answer);
    setBackMemo(content.backMemo);
    setContextAfter(content.contextAfter);
    setCardType(content.cardType);
    setError(null);
  }, [initialData]);

  const applyMarkdownWrapper = (fieldId, value, setValue, prefix) => {
    const textarea = document.getElementById(fieldId);
    if (!textarea) return;
    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || 'text';
    setValue(`${value.slice(0, start)}${prefix}${selected}${prefix}${value.slice(end)}`);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, start + prefix.length + selected.length);
    });
  };

  const copyFieldText = async (fieldId, value) => {
    const textarea = document.getElementById(fieldId);
    const selected = textarea && textarea.selectionEnd > textarea.selectionStart
      ? value.slice(textarea.selectionStart, textarea.selectionEnd)
      : value;
    if (!selected) return;
    try {
      await navigator.clipboard.writeText(selected);
      textarea?.focus();
    } catch {
      setError(new Error(copy('copyError', 'Could not copy text to clipboard.')));
    }
  };

  const renderMarkdownToolbar = (fieldId, value, setValue) => (
    <div className="markup-toolbar" role="toolbar" aria-label={copy('formatting', 'Markup formatting tools')}>
      <button type="button" className="markup-toolbar-btn" onClick={() => applyMarkdownWrapper(fieldId, value, setValue, '**')} disabled={isSubmitting} aria-label={copy('bold', 'Bold')}>B</button>
      <button type="button" className="markup-toolbar-btn" onClick={() => applyMarkdownWrapper(fieldId, value, setValue, '*')} disabled={isSubmitting} aria-label={copy('italic', 'Italic')}>I</button>
      <button type="button" className="markup-toolbar-btn markup-toolbar-icon-btn" onClick={() => copyFieldText(fieldId, value)} disabled={isSubmitting || !value} aria-label={copy('copy', 'Copy')}>
        <Copy size={16} aria-hidden="true" />
      </button>
    </div>
  );

  useEffect(() => {
    if (mode === 'edit') return undefined;
    let cancelled = false;
    apiClient.getAllNotes()
      .then((result) => {
        if (cancelled) return;
        const nextNotes = (Array.isArray(result) ? result : []).filter((note) => (
          note.isEnabled === false &&
          Boolean(note.noteId || note.id) &&
          String(note.noteId || note.id) !== String(initialData?.noteId || '')
        ));
        setAvailableNotes(nextNotes);
        const first = nextNotes[0];
        if (first) {
          setTargetNoteId(String(first.noteId || first.id));
          setTargetMode('existing');
        } else {
          setTargetNoteId('');
          setTargetMode('new');
        }
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError);
      });
    return () => { cancelled = true; };
  }, [initialData?.noteId, mode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    const payload = {
      question: normalizeContent(question),
      answer: normalizeContent(answer),
      ...(mode === 'edit' ? {} : targetMode === 'existing'
        ? { targetNoteId }
        : { newNoteTitle: normalizeContent(newNoteTitle) }),
      ...(mode === 'edit' ? {} : {
        cardType,
        contextPre: normalizeContent(contextPre) || null,
        memo: normalizeContent(memo) || null,
        backMemo: normalizeContent(backMemo) || null,
        contextAfter: normalizeContent(contextAfter) || null,
      }),
    };
    const validationError = validatePayload(payload, mode, copy);
    if (validationError) {
      setError(new Error(validationError));
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const submitAction = mode === 'edit' ? onSubmit : onLevelUp;
      if (typeof submitAction !== 'function') throw new Error(copy('submitError', 'No submit action is available.'));
      await submitAction(mode === 'edit' ? { front: payload.question, back: payload.answer } : payload);
    } catch (submitError) {
      setError(submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`flashcard-form-container level-up-form-container${isLevelUpPage ? ' level-up-form-page-presentation' : ''}`}>
      {mode !== 'edit' && <p className="level-up-form-guidance">{copy('guidance', t('levelUp.guidance'))}</p>}
      {error && <ErrorMessage error={error} onDismiss={() => setError(null)} type={error?.isNetworkError ? 'network' : 'api'} />}

      <form className="flashcard-form level-up-form" onSubmit={handleSubmit}>
        {mode !== 'edit' && (
          <div className="form-group">
            <div className="card-type-switch" role="group" aria-label={copy('cardType', 'Card type')}>
              <button type="button" className={`card-type-switch-option ${cardType === PracticeSettingsService.CARD_TYPES.SIMPLE ? 'active' : ''}`} onClick={() => setCardType(PracticeSettingsService.CARD_TYPES.SIMPLE)} disabled={isSubmitting} aria-pressed={cardType === PracticeSettingsService.CARD_TYPES.SIMPLE}>{copy('short', 'Short')}</button>
              <button type="button" className={`card-type-switch-option ${cardType === PracticeSettingsService.CARD_TYPES.NORMAL ? 'active' : ''}`} onClick={() => setCardType(PracticeSettingsService.CARD_TYPES.NORMAL)} disabled={isSubmitting} aria-pressed={cardType === PracticeSettingsService.CARD_TYPES.NORMAL}>{copy('sentence', 'Sentence')}</button>
            </div>
          </div>
        )}
        {mode !== 'edit' && (
          <div className="context-dialog-row context-dialog-row-before">
            <input
              id="level-up-context-pre"
              className="form-input context-dialog-input"
              aria-label={copy('before', 'Context - Before')}
              value={contextPre}
              onChange={(event) => setContextPre(event.target.value)}
              placeholder={copy('beforePlaceholder', 'Question, situation, or context before the card (optional)')}
              maxLength={500}
              disabled={isSubmitting}
            />
            <button
              type="button"
              className="context-clear-button"
              aria-label={copy('clearBefore', 'Clear context before')}
              title={copy('clearBefore', 'Clear context before')}
              disabled={isSubmitting || !contextPre}
              onClick={() => {
                setContextPre('');
                document.getElementById('level-up-context-pre')?.focus();
              }}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="form-group card-editor-group card-editor-group-front">
          <div className="form-label-row">
            <label htmlFor="level-up-front">{copy('question', mode === 'edit' ? 'Question' : 'Front Side')} <span className="required">*</span></label>
            {renderMarkdownToolbar('level-up-front', question, setQuestion)}
          </div>
          <textarea id="level-up-front" aria-label={`${copy('question', 'Question')} *`} className="form-textarea card-editor-primary" value={question} onChange={(event) => setQuestion(event.target.value)} placeholder={copy('questionPlaceholder', 'Enter the word or sentence to learn')} disabled={isSubmitting} rows={4} required />
        </div>
        {mode !== 'edit' && (
          <div className="form-group card-editor-memo-group card-editor-memo-group-front">
            <input type="text" className="form-input card-editor-memo card-editor-memo-front" aria-label={copy('frontMemo', 'Front memo')} value={memo} onChange={(event) => setMemo(event.target.value)} placeholder={copy('frontMemoPlaceholder', 'Hint for guessing the answer (optional)')} maxLength={500} disabled={isSubmitting} />
          </div>
        )}
        <div className="form-group card-editor-group card-editor-group-back">
          <div className="form-label-row">
            <label htmlFor="level-up-back">{copy('answer', mode === 'edit' ? 'Answer' : 'Back Side')} <span className="required">*</span></label>
            {renderMarkdownToolbar('level-up-back', answer, setAnswer)}
          </div>
          <textarea id="level-up-back" aria-label={`${copy('answer', 'Answer')} *`} className="form-textarea card-editor-primary" value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder={copy('answerPlaceholder', 'Enter the translation or meaning')} disabled={isSubmitting} rows={4} required />
        </div>
        {mode !== 'edit' && (
          <div className="form-group card-editor-memo-group card-editor-memo-group-back">
            <input type="text" className="form-input card-editor-memo card-editor-memo-back" aria-label={copy('backMemo', 'Back memo')} value={backMemo} onChange={(event) => setBackMemo(event.target.value)} placeholder={copy('backMemoPlaceholder', 'Teaching note or grammar explanation (optional)')} maxLength={500} disabled={isSubmitting} />
          </div>
        )}
        {mode !== 'edit' && (
          <div className="context-dialog-row context-dialog-row-after">
            <button
              type="button"
              className="context-clear-button"
              aria-label={copy('clearAfter', 'Clear context after')}
              title={copy('clearAfter', 'Clear context after')}
              disabled={isSubmitting || !contextAfter}
              onClick={() => {
                setContextAfter('');
                document.getElementById('level-up-context-after')?.focus();
              }}
            >
              <X size={18} aria-hidden="true" />
            </button>
            <input
              id="level-up-context-after"
              className="form-input context-dialog-input"
              aria-label={copy('after', 'Context - After')}
              value={contextAfter}
              onChange={(event) => setContextAfter(event.target.value)}
              placeholder={copy('afterPlaceholder', 'Answer context or follow-up sentence (optional)')}
              maxLength={500}
              disabled={isSubmitting}
            />
          </div>
        )}

        {mode !== 'edit' && (
          <fieldset className="level-up-target-fieldset">
            <legend>{copy('destination', 'Destination drill')}</legend>
            {isLevelUpPage && <p className="level-up-target-hint" id="level-up-target-hint">{copy('destinationHint')}</p>}
            <div className="level-up-target-tabs" role="group" aria-label={copy('destinationType', 'Choose destination drill type')}>
              <button type="button" className={targetMode === 'existing' ? 'is-active' : ''} aria-pressed={targetMode === 'existing'} onClick={() => setTargetMode('existing')} disabled={!availableNotes.length || isSubmitting}>{copy('existing', 'Existing')}</button>
              <button type="button" className={targetMode === 'new' ? 'is-active' : ''} aria-pressed={targetMode === 'new'} onClick={() => setTargetMode('new')} disabled={isSubmitting}><FilePlus2 size={16} aria-hidden="true" /> {copy('new', 'New')}</button>
            </div>
            {targetMode === 'existing' ? (
              <select aria-label={isLevelUpPage ? copy('destination') : undefined} aria-describedby={isLevelUpPage ? 'level-up-target-hint' : undefined} value={targetNoteId} onChange={(event) => setTargetNoteId(event.target.value)} disabled={isSubmitting} required>
                {availableNotes.map((note) => (
                  <option key={note.noteId || note.id} value={note.noteId || note.id}>{note.noteTitle || note.title || note.noteId || note.id}</option>
                ))}
              </select>
            ) : (
              <input type="text" aria-label={isLevelUpPage ? copy('newName') : undefined} aria-describedby={isLevelUpPage ? 'level-up-target-hint' : undefined} value={newNoteTitle} onChange={(event) => setNewNoteTitle(event.target.value)} placeholder={copy('newName', 'New drill name')} maxLength={120} disabled={isSubmitting} required />
            )}
          </fieldset>
        )}

        <div className="form-actions level-up-form-actions">
          <button type="submit" className={`btn ${mode === 'edit' ? 'btn-level-up-content-update' : 'btn-level-up-form'}`} disabled={isSubmitting}>
            {mode === 'edit' ? <Save size={16} aria-hidden="true" /> : <ChevronsUp size={16} aria-hidden="true" />}
            <span>{isSubmitting ? (mode === 'edit' ? 'Updating...' : copy('creating', 'Creating...')) : (mode === 'edit' ? 'Update' : copy('create', 'Create Level-Up Card', { level: nextLevel }))}</span>
          </button>
          <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={isSubmitting}>{copy('cancel', 'Cancel')}</button>
        </div>
      </form>
    </div>
  );
}

export default LevelUpFlashCardForm;
